import React, { useMemo, useState } from 'react';
import { PrimaryButton } from 'components/buttons';
import { TagPill } from 'components/badges';
import {
  TAG_NAME_MAX_LENGTH,
  extractTagNamesFromInput,
  normalizeTagName,
  normalizeTagNames,
} from 'constants/tags';
import BaseTextInput from '../BaseTextInput/BaseTextInput';
import InputError from '../InputError/InputError';
import InputLabel from '../InputLabel/InputLabel';

export default function TagInputField({
  labelText = 'Tags',
  name = 'tagNames',
  value = [],
  availableTagNames = [],
  onChange,
  error,
  disabled = false,
}) {
  const [draft, setDraft] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState(-1);
  const tagNames = useMemo(() => normalizeTagNames(value), [value]);
  const normalizedAvailableTagNames = useMemo(
    () => normalizeTagNames(availableTagNames),
    [availableTagNames]
  );
  const pendingDraftSegments = useMemo(() => {
    const draftSegments = draft.split(',');

    return normalizeTagNames(draftSegments.slice(0, -1));
  }, [draft]);
  const activeDraftSegment = useMemo(() => {
    const draftSegments = draft.split(',');

    return normalizeTagName(draftSegments[draftSegments.length - 1]);
  }, [draft]);
  const suggestedTagNames = useMemo(() => {
    if (!activeDraftSegment) {
      return [];
    }

    const selectedTagNames = new Set(tagNames.map((tagName) => tagName.toLowerCase()));
    const searchValue = activeDraftSegment.toLowerCase();

    return normalizedAvailableTagNames
      .filter((tagName) => {
        const normalizedTagName = tagName.toLowerCase();

        return !selectedTagNames.has(normalizedTagName) && normalizedTagName.includes(searchValue);
      })
      .sort((left, right) => {
        const leftStartsWithSearch = left.toLowerCase().startsWith(searchValue);
        const rightStartsWithSearch = right.toLowerCase().startsWith(searchValue);

        if (leftStartsWithSearch !== rightStartsWithSearch) {
          return leftStartsWithSearch ? -1 : 1;
        }

        return left.localeCompare(right);
      })
      .slice(0, 5);
  }, [activeDraftSegment, normalizedAvailableTagNames, tagNames]);
  const showSuggestions = !disabled && isInputFocused && suggestedTagNames.length > 0;
  const activeHighlightedSuggestionIndex =
    showSuggestions && highlightedSuggestionIndex < suggestedTagNames.length
      ? highlightedSuggestionIndex
      : -1;

  const commitTagNames = (nextTagNames) => {
    onChange([...new Set([...tagNames, ...nextTagNames])]);
    setDraft('');
    setHighlightedSuggestionIndex(-1);
  };

  const addTag = () => {
    const nextTags = extractTagNamesFromInput(draft);

    if (nextTags.length === 0) {
      setDraft('');
      return;
    }

    commitTagNames(nextTags);
  };

  const addSuggestedTag = (tagName) => {
    commitTagNames([...pendingDraftSegments, tagName]);
  };

  const removeTag = (tagToRemove) => {
    onChange(tagNames.filter((tagName) => tagName !== tagToRemove));
  };

  const handleKeyDown = (event) => {
    if (showSuggestions && event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedSuggestionIndex((currentIndex) => {
        if (currentIndex >= suggestedTagNames.length - 1) {
          return 0;
        }

        return currentIndex + 1;
      });
      return;
    }

    if (showSuggestions && event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedSuggestionIndex((currentIndex) => {
        if (currentIndex <= 0) {
          return suggestedTagNames.length - 1;
        }

        return currentIndex - 1;
      });
      return;
    }

    if (showSuggestions && event.key === 'Escape') {
      event.preventDefault();
      setIsInputFocused(false);
      setHighlightedSuggestionIndex(-1);
      return;
    }

    if (showSuggestions && event.key === 'Enter' && highlightedSuggestionIndex >= 0) {
      event.preventDefault();
      addSuggestedTag(suggestedTagNames[activeHighlightedSuggestionIndex]);
      return;
    }

    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addTag();
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <InputLabel htmlFor={`${name}-input`}>{labelText}</InputLabel>
        <span className="text-xs text-gray-400">{tagNames.length} selected</span>
      </div>
      <div className="mt-2 rounded-xl border border-white/10 bg-white/5 p-3">
        <p className="text-xs text-gray-500">
          Existing names will be reused. New names will be created automatically when you save the todo.
        </p>

        <div className="mt-3 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <BaseTextInput
              id={`${name}-input`}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                setIsInputFocused(true);
                setHighlightedSuggestionIndex(-1);
              }}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => {
                setIsInputFocused(false);
                setHighlightedSuggestionIndex(-1);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Type a tag and press Enter"
              maxLength={TAG_NAME_MAX_LENGTH}
              disabled={disabled}
              className="flex-1"
              aria-autocomplete="list"
              aria-expanded={showSuggestions}
              aria-controls={showSuggestions ? `${name}-suggestions` : undefined}
              aria-activedescendant={
                activeHighlightedSuggestionIndex >= 0
                  ? `${name}-suggestion-${activeHighlightedSuggestionIndex}`
                  : undefined
              }
            />

            {showSuggestions && (
              <div
                id={`${name}-suggestions`}
                className="absolute inset-x-0 top-full z-10 mt-2 overflow-hidden rounded-xl border border-white/10 bg-gray-900/95 shadow-lg"
              >
                <ul className="max-h-56 overflow-y-auto py-1">
                  {suggestedTagNames.map((tagName, index) => (
                    <li key={tagName}>
                      <button
                        id={`${name}-suggestion-${index}`}
                        type="button"
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseEnter={() => setHighlightedSuggestionIndex(index)}
                        onClick={() => addSuggestedTag(tagName)}
                        className={`block w-full px-3 py-2 text-left text-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-indigo-500 cursor-pointer ${activeHighlightedSuggestionIndex === index ? 'bg-white/10 text-white' : 'text-gray-200 hover:bg-white/10 hover:text-white'}`}
                      >
                        {tagName}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <PrimaryButton
            type="button"
            onClick={addTag}
            disabled={disabled || extractTagNamesFromInput(draft).length === 0}
            className="sm:self-start"
          >
            Add tag
          </PrimaryButton>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {tagNames.length === 0 ? (
            <p className="text-sm text-gray-400">No tags selected yet.</p>
          ) : (
            tagNames.map((tagName) => (
              <TagPill
                key={tagName}
                onRemove={disabled ? undefined : () => removeTag(tagName)}
                disabled={disabled}
              >
                {tagName}
              </TagPill>
            ))
          )}
        </div>
      </div>
      {error && <InputError className="mt-2">{error}</InputError>}
    </div>
  );
}
